import React, { useRef } from "react";
import classnames from "classnames";
import { debounce } from "lodash";

import { calculatePageCount } from "capitalrx-utilities";

import Table, { TableProps } from "../Table";
import NoResultsFound from "../NoResultsFound";
import Pagination from "../Pagination";
import SearchBar from "./SearchBar";
import { createTableLoadingFiller, formatHeaders } from "./helpers";

export enum SortOrder {
  Asc = "asc",
  Desc = "desc",
}

export interface SortOptions {
  [column: string]: SortOrder;
}

export interface QueryOptions {
  full_text_search?: string;
  results_per_page?: number;
  result_count?: string;
  order_by?: Array<SortOptions>;
  page_number?: number;
  expected_fields?: string[];
}

interface Props {
  handleUpdateRequest: (config: QueryOptions) => void;
  isLoading?: boolean;
  paginationRange?: number;
  queryOptions: QueryOptions;
  resultsTotalCount: string | null;
  searchIsEnabled?: boolean;
  highlightActive?: boolean;
  searchLabel?: React.ReactNode;
  searchPlaceholder?: string;
  theme?: string;
  updateDelayTime?: number;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  additionalSearchFilters?: Record<string, any>;
  rowIsClickable?: boolean;
  showDifference?: boolean;
  ableToSort?: boolean;
  testIndex?: string;

  onClickSort?: Function;
}

export interface DataTableProps extends Props, TableProps {}

class MutableDebounceHelper {

  func: null | Function;

  constructor() {
    this.func = null;
  }


  setFunc = (func: Function): void => {
    this.func = func;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  call = (...args: any[]): any => {
    if (this.func !== null) {
      return this.func(...args);
    }
  };
}

interface RefArgs {
  debounce: null | typeof debounce;
  helper: null | MutableDebounceHelper;
}

export const DataTable: React.FC<DataTableProps> = (props: DataTableProps): JSX.Element => {
  const {
    headers,
    data,
    get,
    onClick,
    className,
    handleUpdateRequest,
    isLoading = false,
    paginationRange = 6,
    queryOptions: { full_text_search = "", results_per_page = 20, order_by = [], page_number = 1 },
    searchIsEnabled = true,
    highlightActive = false,
    searchLabel,
    searchPlaceholder,
    theme = "",
    updateDelayTime = 500,
    rowIsClickable = true,
    ableToSort = true,
    showDifference,
    additionalSearchFilters,
    testIndex = "0",
    resultsTotalCount,
    onClickSort,
  } = props;

  const numericResultCount = parseInt(resultsTotalCount);

  const refArgs: RefArgs = {
    debounce: null,
    helper: null,
  };

  const refs = useRef(refArgs).current;

  if (refs.helper === null) {
    refs.helper = new MutableDebounceHelper();
  }

  refs.helper.setFunc(handleUpdateRequest);

  if (refs.debounce === null && refs.helper !== null) {
    refs.debounce = debounce(refs.helper.call, updateDelayTime);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const defaultHandler = (...args: any): void => args;
  const delayedHandleUpdate = refs.debounce || defaultHandler;

  const handleSearchChange = (term: string): void => {
    const filters = {
      full_text_search: term,
      results_per_page,
      order_by,
      page_number: 1,
    };
    delayedHandleUpdate({ ...additionalSearchFilters, ...filters });
  };

  const handleSortChange = (newOrderBy: Array<SortOptions>): void => {
    const filters = {
      ...(searchIsEnabled && { full_text_search }),
      results_per_page,
      order_by: newOrderBy,
      page_number,
    };
    handleUpdateRequest({ ...additionalSearchFilters, ...filters });
  };

  const handlePaginate = (newPageNumber: number): void => {
    const filters = {
      ...(searchIsEnabled && { full_text_search }),
      results_per_page,
      order_by,
      page_number: newPageNumber + 1,
    };
    delayedHandleUpdate({ ...additionalSearchFilters, ...filters });
  };

  const tableContainerClassList = classnames("caprx-data-table-container", `${theme}`, { "caprx-remove-table-row-click": !rowIsClickable });
  const tableClassList = classnames(className, { "is-loading": isLoading });
  const totalPagesOfData = calculatePageCount(numericResultCount, results_per_page);
  const startingDisplayIndex = (page_number - 1) * results_per_page + 1;
  const endingDisplayIndex = startingDisplayIndex + data.length - 1;

  let displayData = data;
  if (isLoading && data.length === 0) {
    displayData = createTableLoadingFiller(headers, results_per_page);
  }

  let countMessage: JSX.Element | string = "";

  if (data.length > 0 && !isLoading) {
    countMessage = (
      <span>
        Showing {startingDisplayIndex}-{endingDisplayIndex} out of {resultsTotalCount} results.
      </span>
    );
  }

  let headerFormat = formatHeaders(headers, order_by, handleSortChange, onClickSort);
  if (!ableToSort) {
    headerFormat = headers;
  }

  return (
    <div className={tableContainerClassList}>
      {searchIsEnabled && (
        <SearchBar
          className="caprx-data-table-search-bar"
          isLoading={isLoading}
          label={searchLabel}
          onChange={handleSearchChange}
          placeholder={searchPlaceholder}
          value={full_text_search}
        />
      )}

      <div className="caprx-data-table-results">
        <Table
          headers={headerFormat}
          data={displayData}
          onClick={onClick}
          get={get}
          className={tableClassList}
          showDifference={showDifference}
          highlightActive={highlightActive}
          testIndex={testIndex}
        />

        {data.length === 0 && !isLoading && <NoResultsFound />}

        <div className="data-table-result-count-message">{countMessage}</div>

        {totalPagesOfData > 1 && (
          <Pagination
            selectedPage={page_number - 1}
            pageCount={totalPagesOfData}
            pageRange={paginationRange}
            handleSelectPage={handlePaginate}
          />
        )}
      </div>
    </div>
  );
};

export default DataTable;
